import {
  batch,
  createEffect,
  createMemo,
  createSignal,
  on,
  type Accessor,
} from 'solid-js';
import type { PropertySetItemProperty } from './PropertySetItem';

export interface UseDeferredFavoritesOptions {
  favoritePropertyKeys: Accessor<readonly string[] | undefined>;
  /** Clears deferred grouping when selection/data reloads. */
  data: Accessor<unknown>;
  onFavoritePropertyKeysChange(keys: string[]): void;
}

export interface UseDeferredFavoritesResult {
  favoriteKeys: Accessor<ReadonlySet<string>>;
  groupedFavoriteKeys: Accessor<ReadonlySet<string>>;
  favoritesListOutdated: Accessor<boolean>;
  toggleFavoriteKey(propertyKey: string): void;
  refreshFavoritesList(): void;
  withFavoriteState(
    properties: readonly PropertySetItemProperty[]
  ): PropertySetItemProperty[];
}

/**
 * @intent Defers favorites section regrouping so starring/unstarring does not
 * shift the list under the cursor. The star fill always reflects live
 * favorites; deferred additions/removals only affect grouping until refresh
 * or a data reload.
 */
export function useDeferredFavorites(
  options: UseDeferredFavoritesOptions
): UseDeferredFavoritesResult {
  const favoriteKeys = createMemo(
    () => new Set(options.favoritePropertyKeys() ?? [])
  );

  const [deferredAdditions, setDeferredAdditions] = createSignal<
    ReadonlySet<string>
  >(new Set<string>());
  const [deferredRemovals, setDeferredRemovals] = createSignal<
    ReadonlySet<string>
  >(new Set<string>());

  const groupedFavoriteKeys = createMemo(() => {
    const favorites = favoriteKeys();
    const additions = deferredAdditions();
    const grouped = new Set<string>();
    for (const key of favorites) if (!additions.has(key)) grouped.add(key);
    for (const key of deferredRemovals()) grouped.add(key);
    return grouped;
  });

  // Self-heal against the live favorites whenever the input changes: a deferred
  // addition only makes sense while the key is still a favorite, and a deferred
  // removal only while it is not. This prunes stale deferrals if an optimistic
  // save is rolled back, and is a no-op for normal save echoes.
  createEffect(
    on(favoriteKeys, (favorites) => {
      batch(() => {
        setDeferredAdditions(
          (prev) => new Set([...prev].filter((key) => favorites.has(key)))
        );
        setDeferredRemovals(
          (prev) => new Set([...prev].filter((key) => !favorites.has(key)))
        );
      });
    })
  );

  // A selection change or data reload rebuilds the list, so nothing stays
  // deferred - the latest favorites are grouped as expected.
  createEffect(
    on(
      options.data,
      () => {
        batch(() => {
          setDeferredAdditions(new Set<string>());
          setDeferredRemovals(new Set<string>());
        });
      },
      { defer: true }
    )
  );

  const favoritesListOutdated = createMemo(
    () => deferredAdditions().size > 0 || deferredRemovals().size > 0
  );

  const toggleFavoriteKey = (propertyKey: string): void => {
    const next = new Set(favoriteKeys());
    const isRemoval = next.has(propertyKey);
    const isClean =
      deferredAdditions().size === 0 && deferredRemovals().size === 0;

    if (isRemoval) {
      next.delete(propertyKey);
      if (deferredAdditions().has(propertyKey)) {
        // Cancel a still-deferred addition.
        setDeferredAdditions(
          (prev) => new Set([...prev].filter((key) => key !== propertyKey))
        );
      } else if (!isClean) {
        // Defer the removal so the layout stays put until a rebuild.
        setDeferredRemovals((prev) => new Set(prev).add(propertyKey));
      }
      // A removal made while the list is in sync regroups immediately.
    } else {
      next.add(propertyKey);
      if (deferredRemovals().has(propertyKey)) {
        // Cancel a still-deferred removal.
        setDeferredRemovals(
          (prev) => new Set([...prev].filter((key) => key !== propertyKey))
        );
      } else {
        // Additions always defer from the grouping.
        setDeferredAdditions((prev) => new Set(prev).add(propertyKey));
      }
    }

    options.onFavoritePropertyKeysChange([...next]);
  };

  const refreshFavoritesList = (): void => {
    batch(() => {
      setDeferredAdditions(new Set<string>());
      setDeferredRemovals(new Set<string>());
    });
  };

  const withFavoriteState = (
    properties: readonly PropertySetItemProperty[]
  ): PropertySetItemProperty[] => {
    const favorites = favoriteKeys();
    return properties.map((property) => ({
      ...property,
      isFavorite: favorites.has(property.key),
    }));
  };

  return {
    favoriteKeys,
    groupedFavoriteKeys,
    favoritesListOutdated,
    toggleFavoriteKey,
    refreshFavoritesList,
    withFavoriteState,
  };
}
